import { PaginationMeta } from '@sr-sdks/api-sdk-axios';
import { Pagination, PaginationRange } from '@sr/ui-library';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

const clampPage = (page: number, totalPages: number) =>
  Math.min(Math.max(page, 1), Math.max(totalPages, 1));

export interface DataTablePaginationProps {
  className?: string;
  /** When true, hides the "X–Y of Z" item count; used for top pagination in tables with dual pagers. */
  hideRange?: boolean;
  isLoading?: boolean;
  onPageRequest?: (page: number, sortBy?: string) => void;
  paginationKey?: string;
  paginationMeta?: PaginationMeta | null;
  sortBy?: string;
}

/**
 * Pagination footer driven off `paginationMeta`, reading/writing its current
 * page from the same URL search-param pattern `AppTableV2` uses. Rendered
 * unconditionally by `DataTable` — renders nothing once there's no data to
 * paginate.
 */
export const DataTablePagination = ({
  className,
  hideRange,
  isLoading,
  onPageRequest,
  paginationKey = 'page',
  paginationMeta,
  sortBy,
}: DataTablePaginationProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const totalPages = paginationMeta?.totalPages ?? 1;
  const currentPage = clampPage(
    Number(searchParams.get(paginationKey)) || 1,
    totalPages,
  );

  // Mirrors AppTableV2: notify the caller of the current page (and current
  // sort) on mount and whenever the URL page param changes, so the follow-up
  // table migrations can keep driving their own data fetch off this callback
  // instead of re-deriving page state themselves.
  useEffect(() => {
    if (!onPageRequest) return;

    onPageRequest(currentPage, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setSearchParams(
      (prev) => {
        prev.set(paginationKey, page.toString());

        return prev;
      },
      { replace: true },
    );
  };

  if (!paginationMeta || paginationMeta.totalItems === 0) return null;

  const justifyClass = hideRange ? 'justify-end' : 'justify-between';

  return (
    <div
      className={twMerge(
        `flex w-full flex-col items-center gap-3 ${justifyClass} sm:flex-row`,
        className,
      )}
    >
      {!hideRange && (
        <PaginationRange
          page={currentPage}
          pageSize={paginationMeta.itemsPerPage}
          totalItems={paginationMeta.totalItems}
        />
      )}
      <Pagination
        currentPage={currentPage}
        disabled={isLoading}
        hasFirstButton={false}
        hasLastButton={false}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
